import { CallStatus } from "@/Case/voip/CallStatus";
import { ActionContext, Module } from "vuex";
import VoipService from "./Voip.Service";
import { Device, Call } from "@twilio/voice-sdk";
import { BaseStore } from "@/Utils/vuex/BaseStore";
import { EmergencyDepartment } from "@/Case/voip/EmergencyDepartment";
import { AudioOutput } from "@/Case/voip/AudioOutput";
import uniqBy from "lodash.uniqby";
import { AxiosError } from "axios";

type TwilioError = {
  message?: string;
  code?: number;
  name?: string;
};

export const NETWORK_ERROR_CANNOT_START_CALL =
  "Can not start call due to connection issue. Please check your network settings.";
export const NETWORK_ERROR_DURING_CALL = "Connection lost. Call ended.";

export interface VoipState {
  emergencyDepartment?: EmergencyDepartment;
  callStatus: CallStatus;
  audioOutputs?: AudioOutput[];
  errorMessage?: string;
  call?: Call;
  muted: Boolean;
  device?: Device;
}

export interface VoipActions {
  startCall(context: ActionContext<VoipState, any>, department: EmergencyDepartment);

  endCall(context: ActionContext<VoipState, any>);

  cleanup(context: ActionContext<VoipState, any>);

  toggleMicrophone(context: ActionContext<VoipState, any>);

  resetError(context: ActionContext<VoipState, any>);

  [key: string]: (context: ActionContext<VoipState, any>, payload: any) => void;
}

export interface VoipStore extends BaseStore<VoipState, VoipActions, any, any> {
  state: VoipState;
  actions: VoipActions;
}

const store = {
  namespaced: true,
  state: {
    callStatus: CallStatus.DISCONNECTED,
    emergencyDepartment: undefined,
    device: undefined,
    muted: false,
    call: undefined,
    errorMessage: undefined,
    audioOutputs: undefined,
  } as VoipState,
  getters: {
    isCallInProgress: (state: VoipState) => state.callStatus !== CallStatus.DISCONNECTED,
    allowsToRecord: (state: VoipState) => state.emergencyDepartment?.allowsToRecord,
  },
  actions: {
    async startCall(context: ActionContext<VoipState, any>, department: EmergencyDepartment) {
      context.commit("setEmergencyDepartment", department);
      context.commit("setCallStatus", CallStatus.CONNECTING);

      try {
        const device: Device = await VoipService.prepareClient();
        context.commit("setDevice", device);

        device.on("error", (error: TwilioError) => {
          console.error("Device error:", error);
          context.commit("setError", error.message || "Device error occurred");
          context.dispatch("endCall");
        });

        const call = await device.connect({ params: { departmentId: department._id } });
        context.commit("setCall", call);

        call.on("accept", () => {
          context.commit("setCallStatus", CallStatus.CONNECTED);
        });

        call.on("disconnect", () => {
          context.dispatch("endCall");
        });

        call.on("cancel", () => {
          context.commit("setError", "Call was cancelled");
          context.dispatch("endCall");
        });

        call.on("reject", () => {
          context.commit("setError", "Call was rejected");
          context.dispatch("endCall");
        });

        call.on("error", (error: TwilioError) => {
          console.error("Call error:", error);
          context.commit("setError", NETWORK_ERROR_DURING_CALL);
          context.dispatch("endCall");
        });
      } catch (error) {
        if ((error as AxiosError).message == "Network Error") {
          context.commit("setError", NETWORK_ERROR_CANNOT_START_CALL);
        } else {
          context.commit("setError", (error as AxiosError).message);
        }
        context.commit("setCallStatus", CallStatus.DISCONNECTED);
      }
    },
    endCall(context: ActionContext<VoipState, any>) {
      context.commit("setCallStatus", CallStatus.DISCONNECTED);
      context.state.call?.disconnect();
      context.dispatch("cleanup");
    },
    cleanup(context: ActionContext<VoipState, any>) {
      const call = context.state.call;
      if (call) {
        call.removeAllListeners();
        context.commit("setCall");
      }

      const device = context.state.device;
      if (device) {
        device.removeAllListeners();
        device.unregister();
        device.destroy();
        context.commit("setDevice");
      }
      context.commit("setEmergencyDepartment");
      context.state.muted = false;
    },
    fillAudioOutputs(context: ActionContext<VoipState, any>) {
      const audio = context.state.device?.audio;
      if (audio) {
        const audioOutputs = Array.from(audio.availableOutputDevices.values(), (it) => it)
          .filter((it) => it.kind === "audiooutput")
          .map((it) => ({
            id: it.deviceId,
            name: it.label,
            groupId: it.groupId,
          }));
        context.commit("setAudioOutputs", uniqBy(audioOutputs, "groupId"));
      }
    },
    setAudioOutputDevice(context: ActionContext<VoipState, any>, deviceId: string) {
      context.state.device?.audio?.speakerDevices.set(deviceId);
    },
    toggleMicrophone(context: ActionContext<VoipState, any>) {
      const call = context.state.call;
      if (call) {
        const muted = call.isMuted();
        call.mute(!muted);
        context.commit("setMuted", !muted);
      }
    },
    resetError(context: ActionContext<VoipState, any>) {
      context.commit("setError");
    },
  } as VoipActions,
  mutations: {
    setCallStatus(state: VoipState, newCallStatus: CallStatus) {
      state.callStatus = newCallStatus;
    },
    setDevice(state: VoipState, device?: Device) {
      state.device = device;
    },
    setError(state: VoipState, error?: string) {
      state.errorMessage = error;
    },
    setMuted(state: VoipState, muted: boolean) {
      state.muted = muted;
    },
    setCall(state: VoipState, call?: Call) {
      state.call = call;
    },
    setEmergencyDepartment(state: VoipState, department: EmergencyDepartment) {
      state.emergencyDepartment = department;
    },
    setAudioOutputs(state: VoipState, audioOutputs: AudioOutput[]) {
      state.audioOutputs = audioOutputs;
    },
  },
} satisfies Module<VoipState, unknown>;

export default store;
